import { motion } from "framer-motion";
import { Construction, ExternalLink } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface StubProps {
  title: string;
  subtitle: string;
  icon: LucideIcon;
  body: string;
  links?: Array<{ label: string; href: string }>;
  upcoming: string[];
}

export default function Stub({ title, subtitle, icon: Icon, body, links, upcoming }: StubProps) {
  return (
    <div className="space-y-6">
      <header>
        <div className="eyebrow">{subtitle}</div>
        <h1 className="text-3xl font-bold mt-1 tracking-tight">{title}</h1>
      </header>

      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass p-8 ring-glass relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-30 pointer-events-none" style={{
          background: "linear-gradient(135deg, rgba(96,165,250,0.08), transparent 70%)",
        }} />
        <div className="relative flex items-start gap-5">
          <div className="w-14 h-14 rounded-2xl bg-white/[0.04] border border-white/[0.06] flex items-center justify-center text-accent-blue">
            <Icon size={26} strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-accent-amber/10 border border-accent-amber/20 text-[11px] font-mono text-accent-amber">
              <Construction size={11} /> WIREFRAME · ITERATION 2
            </div>
            <p className="mt-3 text-slate-300 leading-relaxed max-w-2xl">{body}</p>
            {links && links.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {links.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/[0.06] text-sm text-slate-300 hover:bg-white/[0.07] transition"
                  >
                    {l.label} <ExternalLink size={11} />
                  </a>
                ))}
              </div>
            )}
            <div className="mt-6">
              <div className="eyebrow mb-2">Planned</div>
              <ul className="space-y-1.5">
                {upcoming.map((u) => (
                  <li key={u} className="text-sm text-slate-400 flex items-start gap-2">
                    <span className="block w-1 h-1 rounded-full bg-accent-cyan mt-2" />
                    {u}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
